import { format } from 'date-fns';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';

import type {
  IntegrationHealth,
  IntegrationPublishHealth,
  IntegrationSyncHealth,
} from '@/hooks/useIntegrationHealth';
import type { ConnectedAccount } from '@/hooks/usePlatformIntegrations';
import { STALE_SYNC_AGE_HOURS } from '@/lib/platforms/analytics-capability';
import type { DerivedStatus } from '@/lib/platforms/connection-status';
import {
  formatScopeList,
  shopifyRequiredScopeConsequence,
  shopifyScopeDisplayName,
} from '@/lib/platforms/shopify-permissions';

export type IssueRemedy = 'reconnect' | 'manage' | 'none';

export interface ConnectionIssue {
  title: string;
  detail: string;
  raw?: string | null;
  hard: boolean;
  remedy: IssueRemedy;
  retest?: boolean;
}

interface ConnectionAuthState {
  authFailureCount: number | null;
  lastError: string | null;
}

export function formatDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMM d, yyyy');
}

export function formatAgo(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatDistanceToNow(date, { addSuffix: true });
}

export function newestTimestamp(...values: Array<string | null | undefined>): string | null {
  let newest: { value: string; timestamp: number } | null = null;
  for (const value of values) {
    if (!value) continue;
    const timestamp = Date.parse(value);
    if (!Number.isFinite(timestamp)) continue;
    if (!newest || timestamp > newest.timestamp) newest = { value, timestamp };
  }
  return newest?.value ?? null;
}

export function readableProviderError(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const match = raw.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  const message = match ? match[1].replace(/\\"/g, '"') : raw;
  return message.trim() || null;
}

export interface PlainProviderCause {
  sentence: string;
  remedy: IssueRemedy;
  fixedSince?: boolean;
}

export type ProviderCauseContext = 'publish' | 'sync';

const PUBLISH_PERMISSION_PATTERNS: readonly RegExp[] = [
  /\(#200\)/,
  /pages_manage_posts/,
  /pages_read_engagement/,
  /instagram_content_publish/,
  /does not have permission to publish/,
  /w_member_social/,
  /w_organization_social/,
  /not enough permissions to access/,
  /scope_not_authorized/,
  /has not granted access to this scope/,
  /video\.publish/,
];

function isPublishPermissionDenial(text: string): boolean {
  return PUBLISH_PERMISSION_PATTERNS.some((pattern) => pattern.test(text));
}

export function plainProviderCause(
  raw: string | null | undefined,
  label: string,
  context: ProviderCauseContext,
): PlainProviderCause | null {
  if (!raw) return null;
  const text = raw.toLowerCase();
  const publishing = context === 'publish';
  if (
    publishing &&
    (text.includes('publish_actions') ||
      (text.includes('deprecated') && text.includes('permission')))
  ) {
    return {
      sentence: `${label} rejected that post because the request used a permission ${label} has retired. Mora no longer asks for it, so reconnecting will not change this. Publish again to check.`,
      remedy: 'none',
      fixedSince: true,
    };
  }
  if (/^\s*stale\b|no completion callback/i.test(raw)) {
    return {
      sentence: 'The last sync did not finish. Mora will run it again on the next scheduled sync.',
      remedy: 'none',
    };
  }
  /* Mora's own token lookup (`unified-token-manager.ts`) throws this when it cannot find the stored connection for
     a Company Page; LinkedIn never saw the request. Reconnecting writes the connection again. */
  if (/connection not found:\s*urn:li:/i.test(raw)) {
    return {
      sentence: publishing
        ? `Mora could not find its connection to this Company Page when it tried to post. Reconnect ${label} to fix it.`
        : `Mora could not find its connection to this Company Page when it tried to read data. Reconnect ${label} to fix it.`,
      remedy: 'reconnect',
    };
  }
  if (publishing && isPublishPermissionDenial(text)) {
    return {
      sentence: `${label} did not allow Mora to post to this account. Reconnect ${label} and approve posting.`,
      remedy: 'reconnect',
    };
  }
  return null;
}

export function isPreReconnectAuthFailure(
  sync: IntegrationSyncHealth | null,
  connectionUpdatedAt: string | null,
  connection: ConnectionAuthState,
): boolean {
  if (!sync || sync.lastRunStatus !== 'failed' || sync.lastRunFailureKind !== 'auth') return false;
  if ((connection.authFailureCount ?? 0) !== 0 || connection.lastError) return false;
  if (!sync.lastRunAt || !connectionUpdatedAt) return false;
  const failedAt = Date.parse(sync.lastRunAt);
  const updatedAt = Date.parse(connectionUpdatedAt);
  if (!Number.isFinite(failedAt) || !Number.isFinite(updatedAt)) return false;
  return updatedAt > failedAt;
}

export function syncIssue(
  sync: IntegrationSyncHealth | null,
  label: string,
): ConnectionIssue | null {
  if (!sync || sync.lastRunStatus !== 'failed') return null;
  const plain = plainProviderCause(sync.lastErrorSummary, label, 'sync');
  const error = plain?.sentence ?? readableProviderError(sync.lastErrorSummary);
  const runs =
    sync.failuresSinceSuccess > 1 ? `${sync.failuresSinceSuccess} runs in a row have failed. ` : '';
  const since = sync.lastSuccessAt
    ? `Last succeeded ${formatDay(sync.lastSuccessAt)}.`
    : 'It has never succeeded.';
  return {
    title: 'Data sync is failing',
    detail: `${runs}${since}${error ? ` ${error}` : ''}`,
    raw: sync.lastErrorSummary,
    hard: !sync.lastSuccessAt,
    remedy: plain?.remedy ?? 'reconnect',
  };
}

/** True when the connection was written again (a reconnect) after the failure. */
function reconnectedSince(
  failureAt: string | null | undefined,
  connectionUpdatedAt: string | null | undefined,
) {
  if (!failureAt || !connectionUpdatedAt) return false;
  const failed = Date.parse(failureAt);
  const updated = Date.parse(connectionUpdatedAt);
  return Number.isFinite(failed) && Number.isFinite(updated) && updated > failed;
}

/**
 * The publish failure worth a card, or null. A failure stops being one when a later post reached the channel; when
 * its cause is one Mora has since fixed in code (no step can clear it but a live post, founder 2026-11-01: "there's
 * literally no way to continue here"); or when reconnecting is the fix and the person has reconnected since, the
 * same rule `isPreReconnectAuthFailure` applies to sync. A failure that is still real comes back with the next
 * attempt, with its own cause.
 */
export function publishIssue(
  publish: IntegrationPublishHealth | null,
  label: string,
  connectionUpdatedAt?: string | null,
): ConnectionIssue | null {
  if (!publish || publish.failed === 0) return null;
  const failedAfterLastSuccess =
    publish.lastFailure?.at &&
    publish.lastPublishedAt &&
    Date.parse(publish.lastFailure.at) > Date.parse(publish.lastPublishedAt);
  if (publish.published > 0 && !failedAfterLastSuccess) return null;

  const plain = plainProviderCause(publish.lastFailure?.message, label, 'publish');
  if (plain?.fixedSince) return null;
  if (
    (plain?.remedy ?? 'reconnect') === 'reconnect' &&
    reconnectedSince(publish.lastFailure?.at, connectionUpdatedAt)
  ) {
    return null;
  }
  const error = plain?.sentence ?? readableProviderError(publish.lastFailure?.message);
  const when = formatDay(publish.lastFailure?.at);
  const lastReached =
    publish.published > 0 && publish.lastPublishedAt
      ? `The last post that reached ${label} was on ${formatDay(publish.lastPublishedAt)}.`
      : null;
  return {
    title:
      publish.published === 0
        ? `No post has ever reached ${label}`
        : `The last publish to ${label} failed`,
    detail: [
      `${publish.failed} ${publish.failed === 1 ? 'attempt' : 'attempts'} failed${when ? `, most recently ${when}` : ''}.`,
      lastReached,
      error,
    ]
      .filter(Boolean)
      .join(' '),
    raw: publish.lastFailure?.message,
    hard: publish.published === 0 && !plain?.fixedSince,
    remedy: plain?.remedy ?? 'reconnect',
    retest: plain?.fixedSince === true,
  };
}

/**
 * The "most recent failure" line of a connection's publishing history, in plain words. History, not a call to
 * action: a cause Mora has since fixed says so, and a reconnect-fixable cause says whether the person has
 * reconnected since. Anything unrecognised falls back to the provider's own message.
 */
export function publishFailureSummary(
  failure: { message: string | null; at: string | null } | null | undefined,
  label: string,
  connectionUpdatedAt?: string | null,
): string | null {
  if (!failure?.message) return null;
  const plain = plainProviderCause(failure.message, label, 'publish');
  if (plain?.fixedSince) {
    return `${label} rejected it over a permission ${label} has retired. Mora no longer asks for it.`;
  }
  if (/connection not found:\s*urn:li:/i.test(failure.message)) {
    return `Mora could not find its connection to this Company Page.${
      reconnectedSince(failure.at, connectionUpdatedAt)
        ? ' You have reconnected since.'
        : ` Reconnect ${label} to fix it.`
    }`;
  }
  return plain?.sentence ?? readableProviderError(failure.message);
}

export function staleIssue(
  sync: IntegrationSyncHealth | null,
  fallbackLastSync: string | null,
): ConnectionIssue | null {
  if (sync?.lastRunStatus === 'failed') return null;
  const last = newestTimestamp(sync?.lastSuccessAt, fallbackLastSync);
  if (!last) return null;
  const ageHours = (Date.now() - new Date(last).getTime()) / 3_600_000;
  if (!Number.isFinite(ageHours) || ageHours <= STALE_SYNC_AGE_HOURS) return null;
  return {
    title: 'Data has stopped refreshing',
    detail: `The last successful sync was ${formatAgo(last)}, on ${formatDay(last)}.`,
    hard: false,
    remedy: 'reconnect',
  };
}

function resourcesAllDenied(account: ConnectedAccount): boolean {
  if (account.platform !== 'google-analytics') return false;
  const resources = account.selectedResources ?? [];
  if (account.resourceSelectionMode !== 'selected' || resources.length === 0) return false;
  return resources.every(
    (resource) => resource.syncStatus === 'blocked' || resource.syncStatus === 'failed',
  );
}

function deniedResourcesIssue(
  account: ConnectedAccount,
  lastSuccess: string | null,
): ConnectionIssue {
  const count = account.selectedResources?.length ?? 0;
  const noun = count === 1 ? 'property' : 'properties';
  const since = lastSuccess
    ? `The last successful sync was ${formatAgo(lastSuccess)}.`
    : 'It has never synced.';
  return {
    title: `Google denied access to the selected ${noun}`,
    detail: `Mora cannot read ${count === 1 ? 'it' : 'them'}, so data stopped refreshing. ${since} Choose ${noun} your Google account can open, or check API access with Google. Reconnecting will not change this.`,
    hard: false,
    remedy: 'manage',
  };
}

export function shopifyMissingScopeIssue(missingScopes: readonly string[]): ConnectionIssue | null {
  if (missingScopes.length === 0) return null;
  const names = missingScopes.map(shopifyScopeDisplayName);
  const many = missingScopes.length > 1;
  const consequences = missingScopes.map(shopifyRequiredScopeConsequence).join(' ');
  return {
    title: `Shopify has not granted ${formatScopeList(names)}`,
    detail: `${consequences} Reconnecting Shopify asks for ${many ? 'them' : 'it'} again and removes nothing you have already granted.`,
    hard: false,
    remedy: 'reconnect',
  };
}

export interface ConnectionAttention {
  issues: ConnectionIssue[];
  staleAuthFailure: boolean;
  hasPublishIssue: boolean;
}

export function deriveConnectionAttention(args: {
  platform: string;
  label: string;
  account: ConnectedAccount;
  tokenStatus: DerivedStatus;
  health: IntegrationHealth | null | undefined;
  hasScheduledSync: boolean;
  shopifyMissingScopes?: readonly string[];
}): ConnectionAttention {
  const { label, account, tokenStatus, health, hasScheduledSync } = args;
  const raw = account.rawData as {
    updated_at?: string | null;
    auth_failure_count?: number | null;
  } | null;
  const staleAuthFailure =
    hasScheduledSync && tokenStatus.kind === 'connected'
      ? isPreReconnectAuthFailure(health?.sync ?? null, raw?.updated_at ?? null, {
          authFailureCount: raw?.auth_failure_count ?? null,
          lastError: account.error,
        })
      : false;

  const reconnect: ConnectionIssue | null =
    tokenStatus.kind === 'needs-reconnect'
      ? {
          title: 'Reconnect required',
          detail: tokenStatus.tooltip,
          hard: true,
          remedy: 'reconnect',
        }
      : null;

  const denied = resourcesAllDenied(account);
  const lastSuccess = newestTimestamp(health?.sync?.lastSuccessAt, account.lastSync);
  const sync =
    hasScheduledSync && !staleAuthFailure && !denied
      ? syncIssue(health?.sync ?? null, label)
      : null;
  const stale =
    hasScheduledSync && !denied ? staleIssue(health?.sync ?? null, account.lastSync) : null;
  const deniedIssue =
    hasScheduledSync && denied ? deniedResourcesIssue(account, lastSuccess) : null;
  const publish = publishIssue(health?.publish ?? null, label, raw?.updated_at ?? null);
  const shopify =
    args.platform === 'shopify' ? shopifyMissingScopeIssue(args.shopifyMissingScopes ?? []) : null;

  return {
    issues: [reconnect, sync, deniedIssue, stale, publish, shopify].filter(
      (issue): issue is ConnectionIssue => issue !== null,
    ),
    staleAuthFailure,
    hasPublishIssue: publish !== null,
  };
}

export function attentionLabel(
  issues: readonly ConnectionIssue[],
): 'Not working' | 'Needs attention' | null {
  if (issues.length === 0) return null;
  return issues.some((issue) => issue.hard) ? 'Not working' : 'Needs attention';
}
